import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getServiceURL } from '@src/env.config';
import {
  getJWTToken,
  isJWTExpired,
  removeJWTToken,
} from '@src/utils/tokenManager';

const axiosBaseQuery =
  (): BaseQueryFn<
    AxiosRequestConfig & { body?: any; headers?: any },
    unknown,
    AxiosError
  > =>
  async ({ url, method, body, params: requestParams, headers }) => {
    try {
      let BASE_URL = `${getServiceURL()}`;
      Axios.defaults.baseURL = BASE_URL;
      Axios.defaults.timeout = 600000; // Set default timeout to 60 seconds
      let { isAuth, ...params } = requestParams;

      // Attach JWT token if available and not expired
      let authHeaders = { ...headers };
      const token = getJWTToken();
      if (token && !isJWTExpired()) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }

      const result = await Axios({
        url,
        method,
        ...(body ? { data: body } : {}),
        params,
        // Required so the browser sends the HttpOnly auth cookie
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders, // Merge any additional headers
        },
      });

      return {
        data: result.data,
        meta: {
          headerData: result.headers,
          response: result,
        },
      };
    } catch (axiosError) {
      const error = axiosError as AxiosError;
      // If backend returns 401, JWT is expired/invalid — clear it and redirect to login
      if (error?.response?.status === 401) {
        removeJWTToken();
        window.location.href = '/logout';
      }
      return {
        error,
      };
    }
  };

export const apiService = createApi({
  baseQuery: axiosBaseQuery(),
  endpoints: () => ({}),
});
